/**
 * web/src/hooks/mutations/useGmailPoll.ts
 * Admin testing mutation — runs the inbound Gmail reply-poll on demand
 * (POST /api/admin/gmail-poll) so a confirmed reply flips its tracked email
 * without waiting for the 5-min cron.
 *
 * On success: toasts a one-line summary and invalidates the tracked-emails
 * list(s) + stats so the Confirmations page reflects any newly-confirmed row.
 * The full summary lives on `mutation.data` for the inline results panel.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { triggerGmailPoll, type GmailPollSummary } from '../../api/gmailPoll';
import { toastApiError } from '../../lib/api';
import { qk } from '../../lib/queryKeys';

/**
 * Triggers the Gmail reply-poll once. `mutate(sinceMinutes)` overrides the
 * cron cursor with a last-N-minutes window (testing); `mutate(undefined)`
 * mirrors the cron exactly.
 *
 * @returns React Query mutation handle (variables: minutes | undefined)
 * @sideEffects HTTP POST; on success invalidates tracked-emails + stats caches
 * @idempotent yes — server dedupes re-processed replies
 */
export function useGmailPoll() {
  const qc = useQueryClient();
  return useMutation<GmailPollSummary, unknown, number | undefined>({
    mutationFn: (sinceMinutes) => triggerGmailPoll(sinceMinutes),
    onSuccess: (s) => {
      const confirmed = s.outcomes.classified_auto_confirmed ?? 0;
      toast.success(
        `Polled ${s.users} reviewer${s.users === 1 ? '' : 's'} · fetched ${s.fetched} · ` +
          `processed ${s.processed}${confirmed ? ` · ${confirmed} auto-confirmed` : ''}`,
      );
      // A confident-ack reply confirms a tracked email + resumes its workflow,
      // so refresh the Confirmations list(s) and the badge counts.
      qc.invalidateQueries({ queryKey: qk.trackedEmails.root() });
      qc.invalidateQueries({ queryKey: qk.stats.root() });
    },
    onError: (err) => toastApiError(err, 'Gmail poll failed'),
  });
}
